import { Card } from 'components/Card';
import { ImageField } from 'componentsAware/ImageField';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useSelector } from 'hooks/useSelector';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  eventSelectScreenSelection,
} from 'store/actions';
import { selectPlansContext } from 'store/plan/selectors';
import { doPlansCreatePlan } from 'store/plan/actions';
import { appContextToFileContext } from 'types/types';
import { floorStringToNumber, randomKey } from 'lib/riskbase';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { createId } from '@paralleldrive/cuid2';

export const PlanCreateModal: React.FC<
  ModalScreenProps<ModalScreens.PLANCREATE>
> = () => {
  const dispatch = useDispatch();
  const fieldNameKey = React.useRef(0);
  const eventKeyNameSuggestion = React.useRef(randomKey());

  const context = useSelector(selectPlansContext);
  const [state, setState] = React.useState({ fileId: '', name: '' });

  const handleNameChange = React.useCallback((value: string) => {
    setState((cState) => ({ ...cState, name: value.trim() }));
  }, []);

  const handleImageChange = React.useCallback(
    (fileId: string[]) =>
      fileId.length
        ? setState((cState) => ({ ...cState, fileId: fileId[0]! }))
        : undefined,
    [],
  );

  const handleSavePress = () =>
    dispatch(
      doPlansCreatePlan({
        fileId: state.fileId,
        id: createId(),
        name: state.name,
      }),
    );

  const handleOpenNameSuggestions = () => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          options: floorStringToNumber
            .filter((f) => f.showAsSuggestion)
            .sort((a, b) => a.number - b.number)
            .map((f) => ({
              name: f.label,
              internalValue: f.label,
            })),
          eventKey: eventKeyNameSuggestion.current,
        },
      }),
    );
  };

  const handleClose = () => dispatch(NAVIGATE_MODAL_BACK());

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === eventKeyNameSuggestion.current) {
          const name = event.payload.stringValues[0];
          if (name) {
            fieldNameKey.current += 1;
            setState((existingState) => ({ ...existingState, name }));
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, []);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="New Plan"
          onPressCancelButton={handleClose}
          onPressSaveButton={
            state.name && state.fileId ? handleSavePress : null
          }
        />
      }
    >
      <Card
        label="Name"
        labelLinkText="Suggestions"
        onLabelLinkPress={handleOpenNameSuggestions}
      >
        <TextField
          key={fieldNameKey.current}
          onChange={handleNameChange}
          placeholder="Please enter the plan's name"
          onEsc={handleClose}
          autoFocus
          autoFocusDelay
          value={state.name}
        />
      </Card>
      <Card label="Image">
        <ImageField
          context={appContextToFileContext(context)}
          multiSelect={false}
          onChange={handleImageChange}
          promptMessage="Choose an image for the plan"
          value={state.fileId ? [state.fileId] : []}
        />
      </Card>
    </PageScrollView>
  );
};
